import React, { useEffect, useId, useRef } from 'react';

import type { SystemMessageConfirmationConfirmationProps, SystemMessageConfirmationMessageProps, SystemMessageConfirmationProps } from '../../types/componentType';

const SystemMessageConfirmation: React.FC<SystemMessageConfirmationProps> = (props) => {
  const {
    isOpen,
    isLoading = false,
    title,
    message,
    onCancel,
    type
  } = props;

  const dialogId = useId();
  const titleId = useId();
  const messageId = useId();
  const loadingId = useId();
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Handle Escape key to close dialog
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isLoading, onCancel]);

  // Focus management: focus first button when dialog opens
  useEffect(() => {
    if (isOpen && !isLoading && firstButtonRef.current) {
      firstButtonRef.current.focus();
    }
  }, [isOpen, isLoading]);

  if (!isOpen) return null;

  const getConfirmButtonClasses = () => {
    const baseClasses = 'bsds-p-8 bsds-ph-16 bsds-text-center bsds-text-uppercase bsds-width-100';
    return `${baseClasses} text-white`;
  };

  const getCancelButtonClasses = () => {
    return 'bsds-p-8 bsds-ph-16 bsds-text-center bsds-text-uppercase bsds-width-100 text-gray-700 bg-gray-100 hover:bg-gray-200';
  };

  const getConfirmButtonStyle = () => {
    if (type === 'message') {
      return {
        backgroundColor: 'var(--bsds-content-main-color-primary)',
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        border: 'none',
        cursor: 'pointer'
      };
    }

    const variant = (props as SystemMessageConfirmationConfirmationProps).confirmButtonVariant || 'primary';
    return {
      backgroundColor: variant === 'danger'
        ? 'var(--bsds-content-feedback-danger)'
        : variant === 'secondary'
          ? 'var(--bsds-content-main-color-secondary)'
          : 'var(--bsds-content-main-color-primary)',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
      border: 'none',
      cursor: 'pointer'
    };
  };

  return (
    <div
      className="fixed inset-0 z-[70] bsds-display-flex bsds-justify-content-center bsds-align-items-center bg-black/45"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      id={dialogId}
    >
      <div className="bg-white rounded-lg bsds-p-24 max-w-sm bsds-width-100 bsds-mh-16 shadow-xl">
        <div className="bsds-text-center">
          <h3
            id={titleId}
            className="text-lg font-semibold bsds-mb-16"
          >
            {title}
          </h3>
          <p
            id={messageId}
            className="bsds-mb-24"
          >
            {message}
          </p>

          {isLoading ? (
            <div
              id={loadingId}
              className="bsds-display-flex bsds-flex-direction-column bsds-align-items-center"
              role="status"
              aria-live="polite"
              aria-label="Carregando"
              aria-busy="true"
            >
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                aria-hidden="true"
              />
              <p className="bsds-mt-8 text-sm">
                Carregando...
              </p>
            </div>
          ) : type === 'message' ? (
            <div className="bsds-display-flex bsds-justify-content-center">
              <div className="bsds-width-100">
                <button
                  ref={firstButtonRef}
                  onClick={onCancel}
                  className={getConfirmButtonClasses()}
                  style={getConfirmButtonStyle()}
                  type="button"
                  aria-label={(props as SystemMessageConfirmationMessageProps).buttonText}
                >
                  {(props as SystemMessageConfirmationMessageProps).buttonText}
                </button>
              </div>
            </div>
          ) : (
            <div className="bsds-display-flex bsds-justify-content-center gap-3">
              <div className="bsds-width-50">
                <button
                  ref={firstButtonRef}
                  onClick={onCancel}
                  className={getCancelButtonClasses()}
                  style={{
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  type="button"
                  aria-label={(props as SystemMessageConfirmationConfirmationProps).cancelText}
                >
                  {(props as SystemMessageConfirmationConfirmationProps).cancelText}
                </button>
              </div>
              <div className="bsds-width-50">
                <button
                  onClick={(props as SystemMessageConfirmationConfirmationProps).onConfirm}
                  className={getConfirmButtonClasses()}
                  style={getConfirmButtonStyle()}
                  type="button"
                  aria-label={(props as SystemMessageConfirmationConfirmationProps).confirmText}
                >
                  {(props as SystemMessageConfirmationConfirmationProps).confirmText}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemMessageConfirmation;
